import moment from "moment";
import React from "react";

import ToolTip from "../../../../../parts/tooltip";
import {
  getStartingCalendarDayAdjustment,
  getWeeklyStatsAvg,
  invertColor,
} from "../../../../../utils/annual-planner-helpers";

export const ListView = ({
  dateFromState,
  startingCalendarDay,
  calendarEvents,
  datesAttachments,
  phasesDescriptions,
  athleteStatsState,
  athleteVolumesState,
  classNames = "",
}) => {
  // Getting the first day of the month, and the days amount necessary for the starting calendar day adjustment
  const rangeFirstDay = moment(dateFromState).startOf("month");
  // The amount of days that we have to sum/subtract to adjust to the starting calendar day
  const startAmout = getStartingCalendarDayAdjustment(
    rangeFirstDay,
    startingCalendarDay
  );

  rangeFirstDay.startOf("week").add(startAmout, "days");

  const rangeLastDay = moment(dateFromState).add(11, "months").endOf("month");
  // The amount of days that we have to sum/subtract to adjust to the starting calendar day
  const endAmout = getStartingCalendarDayAdjustment(
    rangeLastDay,
    startingCalendarDay
  );

  rangeLastDay.endOf("week").add(endAmout, "days");

  rangeLastDay.month() !==
    moment(moment(dateFromState).add(2, "months")).month() &&
    rangeLastDay.subtract(1, "week");

  const numberOfWeeks =
    moment(rangeLastDay).diff(moment(rangeFirstDay), "weeks") + 1;

  const sortedEvents = Array.from(calendarEvents)
    .sort((a, b) => {
      if (a.fromDate < b.fromDate) return -1;
      if (b.fromDate < a.fromDate) return 1;
      if (a.fromDate === b.fromDate) return 1;
    })
    .filter(
      (event) =>
        !moment(event.fromDate, "YYYYMMDD").isBefore(
          moment(moment.utc().format("YYYYMMDD"), "YYYYMMDD")
        ) && !moment(event.fromDate, "YYYYMMDD").isBefore(moment(rangeFirstDay))
    );

  const weeksRows = Array.from(Array(numberOfWeeks).keys(), (weekNumber) => {
    const weekFirstDay = moment(rangeFirstDay).add(weekNumber, "weeks");
    const weekLastDay = moment(weekFirstDay).add(6, "days");

    const weekPhase =
      datesAttachments &&
      datesAttachments.find(({ dateAttachment }) =>
        moment(dateAttachment, "YYYYMMDD").isBetween(
          moment(weekFirstDay),
          moment(weekLastDay),
          undefined,
          "[]"
        )
      );

    const weekComment =
      phasesDescriptions &&
      phasesDescriptions.find(({ dateAttachment }) =>
        moment(dateAttachment, "YYYYMMDD").isBetween(
          moment(weekFirstDay),
          moment(weekLastDay),
          undefined,
          "[]"
        )
      );

    // Filtering the current week volumnes from the month array
    const weekVolumes =
      athleteVolumesState &&
      athleteVolumesState.filter((volume) =>
        moment(volume.utcDate, "YYYYMMDD").isBetween(
          moment(weekFirstDay),
          moment(weekLastDay),
          undefined,
          "[]"
        )
      );

    // Filtering the current week stats from the month array
    const weekStats =
      athleteStatsState &&
      athleteStatsState.filter((stat) =>
        moment(stat.utcDate, "YYYYMMDD").isBetween(
          moment(weekFirstDay),
          moment(weekLastDay),
          undefined,
          "[]"
        )
      );

    return {
      weekFirstDay,
      weekLastDay,
      weekPhase,
      weekComment,
      weekStats,
      weekVolumes,
    };
  });

  return (
    <section role="table-wrapper" className={classNames}>
      <table>
        <thead role="table-header">
          <tr>
            <th className="week">Week</th>
            {sortedEvents.length ? (
              <th className="events" colSpan={sortedEvents.length}>
                Events
              </th>
            ) : null}

            <th className="phase">Phase</th>
            <th className="comments">Comments</th>
            <th className="readiness">Readiness</th>
            <th className="session">Session RPE</th>
            <th className="weekly">Weekly Volume</th>
            <th className="body">Body Weight</th>
            <th className="daily">Daily kCal</th>
            <th className="steps">Steps</th>
          </tr>
        </thead>
        <tbody role="table-body">
          {weeksRows.map(
            (
              {
                weekFirstDay,
                weekLastDay,
                weekPhase,
                weekComment,
                weekStats,
                weekVolumes,
              },
              index
            ) => (
              <tr key={`week-row-${index}`}>
                <td>{moment(weekFirstDay).format("MMM D, YYYY")}</td>
                <DataRow
                  index={index}
                  length={weeksRows.length}
                  weekFirstDay={weekFirstDay}
                  weekLastDay={weekLastDay}
                  sortedEvents={sortedEvents}
                  datesAttachments={datesAttachments}
                  phasesDescriptions={phasesDescriptions}
                  weekPhase={weekPhase}
                  weekComment={weekComment}
                  weekStats={weekStats}
                  weekVolumes={weekVolumes}
                />
              </tr>
            )
          )}
        </tbody>
      </table>
    </section>
  );
};

const DataRow = ({
  weekFirstDay,
  weekLastDay,
  sortedEvents,
  weekPhase,
  weekComment,
  weekStats,
  weekVolumes,
  index,
  length,
}) => {
  const {
    volumes,
    readinessAvg,
    sessionRPEAvg,
    bodyWeightAvg,
    kCalAvg,
    stepsAvg,
  } = getWeeklyStatsAvg(weekStats, weekVolumes);

  return (
    <React.Fragment>
      {sortedEvents.length ? (
        <td className="column events" colspan={sortedEvents.length}>
          <div
            style={{ display: "flex" }}
            className={index === length - 1 ? "last" : ""}
          >
            {sortedEvents.length ? (
              sortedEvents.map((event, evInd) => {
                if (
                  event.fromDate &&
                  moment(event.fromDate, "YYYYMMDD").isBetween(
                    moment(weekFirstDay),
                    moment(weekLastDay),
                    undefined,
                    []
                  )
                ) {
                  return (
                    <div
                      key={`counter-wrapper-01-${evInd}`}
                      className="event-week-counter-wrapper"
                    >
                      <ToolTip
                        body={event.name}
                        id={`list-event-${index}-${evInd}`}
                        hintClassNames="tooltip-capitalized"
                        className="annual-planner-tooltip"
                      >
                        <span className="event-week-counter name">
                          {event.name}
                        </span>
                      </ToolTip>
                    </div>
                  );
                }

                if (
                  event.fromDate &&
                  moment(event.fromDate, "YYYYMMDD").isAfter(
                    moment(weekFirstDay)
                  )
                ) {
                  const weeksRemaining = moment(
                    event.fromDate,
                    "YYYYMMDD"
                  ).diff(moment(weekFirstDay), "weeks");

                  const contrastColor = invertColor(event.eventColor);
                  return (
                    <div
                      key={`counter-wrapper-02-${evInd}`}
                      className="event-week-counter-wrapper"
                    >
                      <span
                        className="event-week-counter"
                        style={{
                          backgroundColor: event.eventColor,
                          color: contrastColor,
                        }}
                      >
                        {weeksRemaining}
                      </span>
                    </div>
                  );
                }

                return (
                  <div
                    key={`counter-wrapper-03-${evInd}`}
                    className="event-week-counter-wrapper empty"
                  />
                );
              })
            ) : (
              <div className="no-events" />
            )}
          </div>
        </td>
      ) : null}
      <td className={`column phases${index === length - 1 ? " last" : ""}`}>
        {weekPhase && <span>{weekPhase.phaseName}</span>}
      </td>
      <td className={`column comments${index === length - 1 ? " last" : ""}`}>
        {weekComment && (
          <ToolTip
            body={weekComment.phaseDescription}
            id={`list-week-comment-${index}`}
            className="annual-planner-tooltip"
            hintClassNames="annual-planner-comments"
          >
            <span className="comment-label">
              {weekComment.phaseDescription}
            </span>
          </ToolTip>
        )}
      </td>
      <td className="week-readiness">{readinessAvg || ""}</td>
      <td className="week-sessionrpe">{sessionRPEAvg || ""}</td>
      <td className="week-volumes">{volumes || ""}</td>
      <td className="week-bodyweight">
        {bodyWeightAvg ? `${bodyWeightAvg}kg` : ""}
      </td>
      <td className="week-kcal">{kCalAvg || ""}</td>
      <td className="week-steps">{stepsAvg || ""}</td>
    </React.Fragment>
  );
};
